import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/cn";

/**
 * Tooltip — ui-v2.5. Aclaración breve al hover/foco. Especificación: catálogo · sección Tooltip.
 *
 * POSICIONAMIENTO EN PORTAL (Tanda C): la burbuja se renderiza en un portal a <body> con
 * position:fixed y coordenadas calculadas desde getBoundingClientRect() del disparador. Así ESCAPA
 * de cualquier contenedor con overflow:hidden/scroll (p. ej. las celdas de Table, Drawer, Modal),
 * que era el caso de uso principal (iconos de acción de fila sin texto). z-index 9999 > overlays.
 *
 * Se CIERRA al hacer scroll o resize (con captura, para cualquier contenedor con scroll). Motivo:
 * al scrollear con rueda el cursor deja el disparador y salta mouseleave de todas formas, así que
 * cerrar es el comportamiento estable y coherente (evita burbujas pegadas fuera de su sitio).
 *
 * CRÍTICO (sin cambios): funciona sobre elementos DESHABILITADOS porque el disparador es el <span>
 * envolvente (los handlers están en el span; el elemento disabled con pointer-events-none deja pasar
 * el hover). Se mantienen: retardo 350ms (hover) / inmediato (foco), 4 posiciones, role="tooltip" +
 * aria-describedby en el disparador.
 *
 * DEUDA (declarada): sin auto-flip por colisión con el borde de la ventana. El portal ya resuelve el
 * recorte por contenedor (el bloqueante real); el auto-flip exigiría medir la burbuja y un segundo
 * pase de posicionamiento (riesgo de parpadeo), y los iconos de acción rara vez tocan el borde del
 * viewport. Se deja como deuda pequeña; se puede añadir midiendo la burbuja en useLayoutEffect.
 *
 * <Tooltip content="Texto" placement="top|bottom|left|right"><button disabled>…</button></Tooltip>
 */

const GAP = 8;

// Posición fixed + transform de centrado, sin necesidad de medir la burbuja.
function computePosition(rect, placement) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  switch (placement) {
    case "bottom":
      return { left: cx, top: rect.bottom + GAP, transform: "translate(-50%, 0)" };
    case "left":
      return { left: rect.left - GAP, top: cy, transform: "translate(-100%, -50%)" };
    case "right":
      return { left: rect.right + GAP, top: cy, transform: "translate(0, -50%)" };
    case "top":
    default:
      return { left: cx, top: rect.top - GAP, transform: "translate(-50%, -100%)" };
  }
}

// Flecha de 5px del mismo color (#0f172a = ink-900), relativa a la burbuja.
const ARROW_POS = {
  top: "top-full left-1/2 -translate-x-1/2 border-x-[5px] border-t-[5px] border-x-transparent border-t-v25-ink-900",
  bottom: "bottom-full left-1/2 -translate-x-1/2 border-x-[5px] border-b-[5px] border-x-transparent border-b-v25-ink-900",
  left: "left-full top-1/2 -translate-y-1/2 border-y-[5px] border-l-[5px] border-y-transparent border-l-v25-ink-900",
  right: "right-full top-1/2 -translate-y-1/2 border-y-[5px] border-r-[5px] border-y-transparent border-r-v25-ink-900",
};

export function Tooltip({ content, placement = "top", children, className }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const timer = useRef(null);
  const triggerRef = useRef(null);
  const id = useId();

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (el) setPos(computePosition(el.getBoundingClientRect(), placement));
  }, [placement]);

  const openDelayed = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      reposition();
      setOpen(true);
    }, 350);
  };
  const openNow = () => {
    clearTimeout(timer.current);
    reposition();
    setOpen(true);
  };
  const close = () => {
    clearTimeout(timer.current);
    setOpen(false);
  };

  // Cerrar en scroll (captura → cualquier contenedor con scroll) y en resize mientras esté visible.
  useEffect(() => {
    if (!open) return undefined;
    const closeOnMove = () => {
      clearTimeout(timer.current);
      setOpen(false);
    };
    window.addEventListener("scroll", closeOnMove, true);
    window.addEventListener("resize", closeOnMove);
    return () => {
      window.removeEventListener("scroll", closeOnMove, true);
      window.removeEventListener("resize", closeOnMove);
    };
  }, [open]);

  // Limpia el temporizador al desmontar (evita setState tras desmontar).
  useEffect(() => () => clearTimeout(timer.current), []);

  if (!content) return children;

  const trigger = React.isValidElement(children)
    ? React.cloneElement(children, { "aria-describedby": open ? id : undefined })
    : children;

  return (
    <span
      ref={triggerRef}
      className={cn("inline-flex", className)}
      onMouseEnter={openDelayed}
      onMouseLeave={close}
      onFocus={openNow}
      onBlur={close}
    >
      {trigger}
      {open && pos &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            style={{ position: "fixed", left: pos.left, top: pos.top, transform: pos.transform, zIndex: 1010 }}
            className="pointer-events-none max-w-[260px] whitespace-nowrap rounded-v25-sm bg-v25-ink-900 px-2.5 py-1.5 text-xs text-white shadow-[0_4px_12px_rgba(0,0,0,0.18)]"
          >
            {content}
            <span className={cn("absolute h-0 w-0", ARROW_POS[placement] || ARROW_POS.top)} />
          </span>,
          document.body
        )}
    </span>
  );
}
